import Slide from '@material-ui/core/Slide';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ANTICIPATION_AUDIT_TRAIL_ENABLED } from '../../../../../config/constants';
import ActionButton from '../../../../../components/buttons/action-button/action-button';
import { BatchToRequest } from '../../../../../model/batch';
import { CreditToRequest } from '../../../../../model/credit';
import { HttpRequestStatus } from '../../../../../model/enums/httpRequestStatus';
import { requestBatchRequest } from '../../../../../reducer/batch/request/actions';
import { useBatchRequestState, useEstimateRequestState, useRootDispatch } from '../../../../../reducer/hooks';
import { AnticipationAuditTrail, collectAuditTrail, geolocationPermission } from '../../../../../shared/util/browser-audit';
import AnticipationTermSession from '../anticipation-term-session/anticipation-term-session';
import GeolocationPermissionModal, { GeolocationPermissionModalState } from '../geolocation-permission-modal/geolocation-permission-modal';
import './anticipation-request-confirmation.scss';

export interface AnticipationRequestConfirmationProps {
    open: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    disabled: boolean;
    time?: Date;
}

const useBatchRequestStateAwareness = () => {
    const state = useBatchRequestState();

    const [isLoading, setLoading] = React.useState<boolean>(false);
    const hasError = state.status === HttpRequestStatus.ERROR;

    React.useEffect(() => {
        if (state.status === HttpRequestStatus.ON_GOING) {
            setLoading(true);
        } else if (state.status === HttpRequestStatus.SUCCESS || state.status === HttpRequestStatus.ERROR) {
            setLoading(false);
        }
    }, [state.status]);

    return { isLoading, hasError };
};

export const AnticipationRequestConfirmation = (props: AnticipationRequestConfirmationProps) => {
    const { open, onCancel, onConfirm, disabled, time } = props;
    const { t } = useTranslation();
    const estimateState = useEstimateRequestState();
    const batchState = useBatchRequestState();

    const dispatch = useRootDispatch();
    const { isLoading } = useBatchRequestStateAwareness();

    const [isCollecting, setCollecting] = React.useState<boolean>(false);
    const [permissionModal, setPermissionModal] = React.useState<GeolocationPermissionModalState | undefined>(undefined);

    const requestAnticipation = async () => {
        setPermissionModal(undefined);
        const _credits: CreditToRequest[] = estimateState?.estimate?.creditEstimates.map(it => ({ id: it.id })) ?? [];

        let auditTrail: AnticipationAuditTrail | undefined;
        if (ANTICIPATION_AUDIT_TRAIL_ENABLED) {
            setCollecting(true);
            try {
                auditTrail = await collectAuditTrail();
            } catch {
                auditTrail = undefined;
            } finally {
                setCollecting(false);
            }
        }

        const request: BatchToRequest = { credits: _credits, auditTrail };
        dispatch(requestBatchRequest(request));
    };

    // A explicacao vem antes do prompt do navegador: negado uma vez, o navegador nao pergunta de novo.
    // Localizacao continua opcional -- negar, ignorar ou "continuar sem" nunca impede a antecipacao.
    const handleConfirmation = async () => {
        if (!ANTICIPATION_AUDIT_TRAIL_ENABLED) {
            await requestAnticipation();
            return;
        }

        const permission = await geolocationPermission();

        if (permission === 'prompt' || permission === 'denied') {
            setPermissionModal(permission);
            return;
        }

        await requestAnticipation();
    };

    const handleRetry = async () => {
        const permission = await geolocationPermission();

        if (permission === 'denied') {
            setPermissionModal('denied');
            return;
        }

        await requestAnticipation();
    };

    React.useEffect(() => {
        if (batchState.status === HttpRequestStatus.SUCCESS) {
            onConfirm();
        }
    }, [batchState.status, onConfirm]);

    return (
        <Slide in={open} direction="up" timeout={500} unmountOnExit>
            <div className="anticipation-request-confimation">
                <div className="body__confirmation--itens">
                    {disabled ? t('anticipation-simulation.anticipation-request-confirmation.time-limit', {time: moment(time).format('HH:mm') ?? undefined}) : 
                    (
                        <><div className="body__confirmation--content">
                                <div className="body__confirmation--image" />
                            </div><div className="body__confirmation--title-start">
                                    <span className="body__confirmation--title-start-one">
                                        {t('anticipation-simulation.anticipation-request-confirmation.start')}
                                    </span>
                                    <br />
                                    <span className="body__confirmation--title-start-two">
                                        {t('anticipation-simulation.anticipation-request-confirmation.days-to-pay', {
                                            count: estimateState.estimate?.daysToPay ?? 0
                                        })}
                                    </span>
                                    {t('anticipation-simulation.anticipation-request-confirmation.end')}
                                </div></>
                    )}
                    

                    <div className="body__confirmation--terms">
                        <AnticipationTermSession />
                    </div>
                    <div className="body__confirmation--content">
                        <div className="body__confirmation--button">
                            <button className="body__confirmation--button-cancel" onClick={onCancel}>
                                {t('anticipation-simulation.anticipation-request-confirmation.button.cancel').toUpperCase()}
                            </button>
                            <ActionButton
                                label={t(
                                    'anticipation-simulation.anticipation-request-confirmation.button.confirm'
                                ).toUpperCase()}
                                onClick={handleConfirmation}
                                disabled={disabled}
                                isLoading={isLoading || isCollecting}
                            />
                        </div>
                    </div>
                </div>
                <GeolocationPermissionModal
                    state={permissionModal}
                    onConfirm={requestAnticipation}
                    onRetry={handleRetry}
                    onContinueWithout={requestAnticipation}
                />
            </div>
        </Slide>
    );
};

export default AnticipationRequestConfirmation;
